import { useEffect, useMemo, useRef } from 'react';
import styles from './RichText.module.scss';
import cx from 'classnames';

// Wraps the content of each <li> in a <span> to keep browser's default behavior from
// breaking lines inside <li> when there's custom bullet
function wrapUnorderedListItems(container: HTMLElement): void {
	container.querySelectorAll('li').forEach((li) => {
		// Skip if we've already wrapped
		if (li.querySelector(':scope > span[data-li-wrap]')) return;

		const span = document.createElement('span');
		span.setAttribute('data-li-wrap', '');

		// Move all nodes except nested lists (<ul>/<ol>) into the span
		let node: ChildNode | null = li.firstChild;
		while (node) {
			const next = node.nextSibling;
			const isNestedList = node.nodeType === Node.ELEMENT_NODE && ['UL', 'OL'].includes((node as Element).tagName); // keep sublists outside the span

			if (!isNestedList) span.appendChild(node);
			node = next;
		}

		if (span.childNodes.length) li.insertBefore(span, li.firstChild);
	});
}

export interface RichText {
	content: string;
	className?: string;
	richTextRef?: React.RefObject<HTMLDivElement>;
	variant?: 'default' | 'website';
}

export const RichText: React.FC<RichText> = ({ content, className, richTextRef, variant = 'default' }) => {
	const internalRef = useRef<HTMLDivElement>(null);
	const ref = richTextRef || internalRef;
	// React 19 resets innerHTML whenever this object changes, which would undo DOM changes made after render
	const html = useMemo(() => ({ __html: content }), [content]);

	// Only wrap on client-side after hydration
	useEffect(() => {
		if (ref.current) {
			wrapUnorderedListItems(ref.current);
		}
	}, [ref]);

	return (
		<div ref={ref} className={cx(styles.RichText, styles[`RichText___${variant}`], className)} dangerouslySetInnerHTML={html} />
	);
};
